// SosAlertsPage — full SOS history: active/acknowledged cards up top,
// resolved/cancelled history table below.

import { useEffect, useState } from 'react';
import { getSosAlerts, acknowledgeSos, resolveSos } from '../api/client';
import SosLocationModal from './SosLocationModal';
import Badge from '../ui/Badge';
import { Card, CardHeader } from '../ui/Card';

function timeAgo(iso) {
  const seconds = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ago`;
}

function responseTime(alert) {
  if (!alert.acknowledged_at) return '—';
  const ms = new Date(alert.acknowledged_at) - new Date(alert.triggered_at);
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  return `${min} min ${sec} sec`;
}

const STATUS_TONE = {
  active:       'critical',
  acknowledged: 'caution',
  resolved:     'success',
  cancelled:    'neutral',
};

const STATUS_LABEL = {
  active:       'Active',
  acknowledged: 'Acknowledged',
  resolved:     'Resolved',
  cancelled:    'False Alarm',
};

export default function SosAlertsPage() {
  const [alerts, setAlerts]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewing, setViewing] = useState(null);
  const [msg, setMsg] = useState('');

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    try {
      setAlerts(await getSosAlerts('all'));
    } catch {
      setMsg('Failed to load SOS alerts');
    }
    setLoading(false);
  }

  async function handleAcknowledge(id) {
    try { await acknowledgeSos(id); setViewing(null); load(); } catch {}
  }

  async function handleResolve(id) {
    try { await resolveSos(id); setViewing(null); load(); } catch {}
  }

  const live    = alerts.filter(a => a.status === 'active' || a.status === 'acknowledged');
  const history = alerts.filter(a => a.status === 'resolved' || a.status === 'cancelled');

  return (
    <div>
      <h2 className="font-display font-extrabold text-xl text-ink mb-4">SOS Alerts</h2>
      {msg && (
        <p className="text-sm font-bold px-3 py-2 rounded-s mb-4 inline-block bg-critical-tint text-critical">{msg}</p>
      )}
      {loading && <p className="text-ink-soft">Loading…</p>}

      <h3 className="font-display font-bold text-[15px] text-ink-soft mt-0 mb-2">Active &amp; Acknowledged</h3>
      {!loading && live.length === 0 && (
        <p className="text-ink-soft mb-6">No active emergencies right now.</p>
      )}
      <div className="flex flex-col gap-3 mb-8">
        {live.map(a => (
          <div key={a.id}
            className={`bg-paper-raised border border-line shadow-1 rounded-m p-3.5 border-l-4 ${a.status === 'active' ? 'border-l-critical' : 'border-l-caution'}`}>
            <div className="flex justify-between items-center">
              <strong className="text-ink">🚨 {a.driver_name || 'Unknown driver'}</strong>
              <span className="text-ink-soft text-xs">{timeAgo(a.triggered_at)}</span>
            </div>
            <div className="text-[13px] text-ink-soft">Route: {a.route_name || 'Route unknown'}</div>
            <div className="text-[13px] text-ink-soft">Reason: {a.message}</div>
            <div className="flex justify-between items-center mt-1">
              <span className="text-xs text-ink-soft">
                📍 {Number(a.latitude).toFixed(5)}, {Number(a.longitude).toFixed(5)}
              </span>
              <button onClick={() => setViewing(a)}
                className="bg-transparent border-none text-transit-deep cursor-pointer text-xs font-bold p-0 hover:underline">
                View Map
              </button>
            </div>
            <div className="flex gap-2 mt-2">
              {a.status === 'active' && (
                <button onClick={() => handleAcknowledge(a.id)}
                  className="bg-caution-tint text-caution font-bold px-3 py-1.5 rounded-s text-[13px]">
                  Acknowledge
                </button>
              )}
              <button onClick={() => handleResolve(a.id)}
                className="bg-success-tint text-success font-bold px-3 py-1.5 rounded-s text-[13px]">
                Resolve
              </button>
            </div>
          </div>
        ))}
      </div>

      <Card>
        <CardHeader title="History" />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] font-bold uppercase tracking-wider text-ink-soft bg-paper">
                {['Driver', 'Reason', 'Triggered', 'Acknowledged By', 'Response Time', 'Status'].map(h => (
                  <th key={h} className="px-4 py-2.5">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {history.map(a => (
                <tr key={a.id} className="border-t border-line-soft hover:bg-transit-tint/40">
                  <td className="px-4 py-3">{a.driver_name || '—'}</td>
                  <td className="px-4 py-3">{a.message}</td>
                  <td className="px-4 py-3">{new Date(a.triggered_at).toLocaleString()}</td>
                  <td className="px-4 py-3">{a.acknowledged_by_name || '—'}</td>
                  <td className="px-4 py-3">{responseTime(a)}</td>
                  <td className="px-4 py-3">
                    <Badge tone={STATUS_TONE[a.status] || 'neutral'}>{STATUS_LABEL[a.status] || 'False Alarm'}</Badge>
                  </td>
                </tr>
              ))}
              {!loading && history.length === 0 && (
                <tr><td className="px-4 py-8 text-center text-ink-soft" colSpan={6}>No past alerts yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {viewing && (
        <SosLocationModal
          alert={viewing}
          onClose={() => setViewing(null)}
          onAcknowledge={handleAcknowledge}
          onResolve={handleResolve}
        />
      )}
    </div>
  );
}
